// Define interfaces for type safety
interface Question {
  id: string;
  type: string;
  question: string;
  options?: string[];
  correctAnswer: any;
  correctAnswers?: number[];
  explanation: string;
  points: number;
  codeTemplate?: string;
}

interface Quiz {
  id: string;
  title: string;
  description: string;
  courseId: string;
  courseName: string;
  status: 'published' | 'draft';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  timeLimit: number;
  passingScore: number;
  maxAttempts: number;
  totalQuestions: number;
  questions: Question[];
  attempts: number;
  averageScore: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  tags: string[];
  category: string;
}

interface Rubric {
  criteria: string;
  maxPoints: number;
  description: string;
}

interface Assignment {
  id: string;
  title: string;
  description: string;
  courseId: string;
  courseName: string;
  status: 'published' | 'draft';
  difficulty: 'intermediate' | 'advanced';
  dueDate: string;
  maxPoints: number;
  submissionType: string;
  allowedFileTypes: string[];
  maxFileSize: string;
  instructions: string;
  submissions: number;
  averageScore: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  tags: string[];
  category: string;
  rubric: Rubric[];
}

interface QuestionBankItem {
  id: string;
  type: string;
  question: string;
  options?: string[];
  correctAnswer: any;
  correctAnswers?: number[];
  explanation: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  category: string;
  tags: string[];
  points: number;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  codeTemplate?: string;
}

interface QuizAnalytics {
  quizId: string;
  totalAttempts: number;
  averageScore: number;
  passRate: number;
  averageTimeSpent: number;
  completionRate: number;
  questionAnalytics: {
    questionId: string;
    correctAnswers: number;
    totalAnswers: number;
    accuracyRate: number;
    averageTime: number;
  }[];
  difficultyBreakdown: {
    easy: { questions: number; averageScore: number };
    medium: { questions: number; averageScore: number };
    hard: { questions: number; averageScore: number };
  };
  timeDistribution: {
    [key: string]: number;
  };
}

interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
}

interface QuestionType {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface DifficultyLevel {
  id: string;
  name: string;
  description: string;
  color: string;
  pointMultiplier: number;
}

interface MockAssessmentData {
  quizzes: Quiz[];
  assignments: Assignment[];
  questionBank: QuestionBankItem[];
  quizAnalytics: QuizAnalytics[];
  categories: Category[];
  questionTypes: QuestionType[];
  difficultyLevels: DifficultyLevel[];
}

export const mockAssessmentData: MockAssessmentData = {
  quizzes: [
    {
      id: 'quiz_1',
      title: 'JavaScript Fundamentals',
      description: 'Test your understanding of JavaScript basics including variables, functions, and objects.',
      courseId: '1',
      courseName: 'Advanced JavaScript Programming',
      status: 'published',
      difficulty: 'beginner',
      timeLimit: 30, // minutes
      passingScore: 70,
      maxAttempts: 3,
      totalQuestions: 10,
      questions: [
        {
          id: 'q1',
          type: 'multiple_choice',
          question: 'Which of the following is NOT a primitive data type in JavaScript?',
          options: ['string', 'number', 'object', 'boolean'],
          correctAnswer: 2, // index
          explanation: 'Objects are not primitive data types in JavaScript. Primitive types include string, number, boolean, undefined, null, symbol, and bigint.',
          points: 1,
        },
        {
          id: 'q2',
          type: 'multiple_choice',
          question: 'What does the "this" keyword refer to in JavaScript?',
          options: ['The current function', 'The global object', 'The current object', 'It depends on the context'],
          correctAnswer: 3,
          explanation: 'The value of "this" in JavaScript depends on how the function is called and the execution context.',
          points: 1,
        },
        {
          id: 'q3',
          type: 'true_false',
          question: 'JavaScript is a statically typed language.',
          correctAnswer: false,
          explanation: 'JavaScript is a dynamically typed language, meaning variable types are determined at runtime.',
          points: 1,
        },
        {
          id: 'q4',
          type: 'multiple_answer',
          question: 'Which of the following are valid ways to declare a variable in JavaScript?',
          options: ['var name', 'let age', 'const PI', 'int count'],
          correctAnswers: [0, 1, 2], // indices
          explanation: 'var, let, and const are valid ways to declare variables in JavaScript. "int" is not a JavaScript keyword.',
          points: 2,
        },
        {
          id: 'q5',
          type: 'fill_in_blank',
          question: 'The _____ method is used to add an element to the end of an array.',
          correctAnswer: 'push',
          explanation: 'The push() method adds one or more elements to the end of an array.',
          points: 1,
        },
      ],
      attempts: 245,
      averageScore: 78.5,
      createdAt: '2024-11-15T10:30:00Z',
      updatedAt: '2024-12-01T14:20:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['javascript', 'fundamentals', 'programming'],
      category: 'programming',
    },
    {
      id: 'quiz_2',
      title: 'React Components & Props',
      description: 'Evaluate your knowledge of React components, props, and component lifecycle.',
      courseId: '2',
      courseName: 'React Development Masterclass',
      status: 'published',
      difficulty: 'intermediate',
      timeLimit: 45,
      passingScore: 75,
      maxAttempts: 2,
      totalQuestions: 15,
      questions: [], // Would contain detailed questions
      attempts: 189,
      averageScore: 82.3,
      createdAt: '2024-11-20T09:15:00Z',
      updatedAt: '2024-12-05T16:45:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['react', 'components', 'props', 'frontend'],
      category: 'frontend',
    },
    {
      id: 'quiz_3',
      title: 'Database Design Principles',
      description: 'Test your understanding of database normalization, relationships, and SQL queries.',
      courseId: '3',
      courseName: 'Database Management Systems',
      status: 'draft',
      difficulty: 'advanced',
      timeLimit: 60,
      passingScore: 80,
      maxAttempts: 1,
      totalQuestions: 20,
      questions: [], // Would contain detailed questions
      attempts: 0,
      averageScore: 0,
      createdAt: '2024-12-08T11:00:00Z',
      updatedAt: '2024-12-08T11:00:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['database', 'sql', 'normalization', 'backend'],
      category: 'backend',
    },
    {
      id: 'quiz_4',
      title: 'Python Data Structures',
      description: 'Quiz on Python lists, dictionaries, sets, and tuples.',
      courseId: '4',
      courseName: 'Python for Data Science',
      status: 'published',
      difficulty: 'beginner',
      timeLimit: 25,
      passingScore: 65,
      maxAttempts: 3,
      totalQuestions: 8,
      questions: [], // Would contain detailed questions
      attempts: 156,
      averageScore: 71.2,
      createdAt: '2024-11-25T13:45:00Z',
      updatedAt: '2024-12-03T10:20:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['python', 'data-structures', 'programming'],
      category: 'programming',
    },
  ],

  assignments: [
    {
      id: 'assign_1',
      title: 'Build a Todo List App',
      description: 'Create a fully functional todo list application using React with the following features: add/remove tasks, mark as complete, filter tasks, and persist data in localStorage.',
      courseId: '2',
      courseName: 'React Development Masterclass',
      status: 'published',
      difficulty: 'intermediate',
      dueDate: '2024-12-20T23:59:59Z',
      maxPoints: 100,
      submissionType: 'file_upload',
      allowedFileTypes: ['.zip', '.rar', '.tar.gz'],
      maxFileSize: '50MB',
      instructions: `
## Requirements:
1. Create a React application with the following functionality:
   - Add new tasks
   - Mark tasks as complete/incomplete
   - Delete tasks
   - Filter tasks (All, Active, Completed)
   - Persist tasks in localStorage

## Evaluation Criteria:
- Code quality and organization (30 points)
- Functionality completeness (40 points)
- User interface design (20 points)
- Code comments and documentation (10 points)

## Submission:
- Submit your complete project as a ZIP file
- Include a README.md with setup instructions
- Ensure your application runs without errors
      `,
      submissions: 34,
      averageScore: 84.5,
      createdAt: '2024-11-18T14:30:00Z',
      updatedAt: '2024-12-02T09:15:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['react', 'javascript', 'project', 'frontend'],
      category: 'frontend',
      rubric: [
        {
          criteria: 'Code Quality & Organization',
          maxPoints: 30,
          description: 'Clean, readable, and well-organized code with proper component structure',
        },
        {
          criteria: 'Functionality',
          maxPoints: 40,
          description: 'All required features working correctly without bugs',
        },
        {
          criteria: 'User Interface',
          maxPoints: 20,
          description: 'Intuitive and visually appealing user interface',
        },
        {
          criteria: 'Documentation',
          maxPoints: 10,
          description: 'Clear comments and comprehensive README file',
        },
      ],
    },
    {
      id: 'assign_2',
      title: 'Database Schema Design',
      description: 'Design a normalized database schema for an e-commerce platform including tables for users, products, orders, and inventory.',
      courseId: '3',
      courseName: 'Database Management Systems',
      status: 'published',
      difficulty: 'advanced',
      dueDate: '2024-12-25T23:59:59Z',
      maxPoints: 150,
      submissionType: 'text_and_file',
      allowedFileTypes: ['.sql', '.pdf', '.doc', '.docx'],
      maxFileSize: '25MB',
      instructions: `
## Assignment: E-commerce Database Design

Design a complete database schema for an e-commerce platform.

### Requirements:
1. Create tables for: Users, Products, Categories, Orders, Order Items, Reviews, Shopping Cart
2. Implement proper normalization (at least 3NF)
3. Define primary and foreign keys
4. Create appropriate indexes
5. Write sample SQL queries for common operations

### Deliverables:
1. SQL file with CREATE TABLE statements
2. ER diagram (as PDF or image)
3. Documentation explaining your design decisions
4. 10 sample queries with explanations

### Evaluation:
- Schema design and normalization (60 points)
- Documentation quality (40 points)
- SQL query correctness (30 points)
- ER diagram clarity (20 points)
      `,
      submissions: 28,
      averageScore: 78.9,
      createdAt: '2024-11-22T10:15:00Z',
      updatedAt: '2024-12-01T15:30:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['database', 'sql', 'design', 'backend'],
      category: 'backend',
      rubric: [
        {
          criteria: 'Schema Design & Normalization',
          maxPoints: 60,
          description: 'Proper database design following normalization principles',
        },
        {
          criteria: 'Documentation',
          maxPoints: 40,
          description: 'Clear explanation of design decisions and rationale',
        },
        {
          criteria: 'SQL Queries',
          maxPoints: 30,
          description: 'Correct and efficient SQL queries for common operations',
        },
        {
          criteria: 'ER Diagram',
          maxPoints: 20,
          description: 'Clear and accurate entity-relationship diagram',
        },
      ],
    },
    {
      id: 'assign_3',
      title: 'Data Analysis with Python',
      description: 'Analyze a provided dataset using Python pandas and create visualizations to answer specific business questions.',
      courseId: '4',
      courseName: 'Python for Data Science',
      status: 'published',
      difficulty: 'intermediate',
      dueDate: '2024-12-18T23:59:59Z',
      maxPoints: 80,
      submissionType: 'file_upload',
      allowedFileTypes: ['.ipynb', '.py', '.zip'],
      maxFileSize: '30MB',
      instructions: `
## Data Analysis Assignment

Analyze the provided sales dataset and answer the following questions:

### Questions to Answer:
1. What are the top 5 best-selling products?
2. Which month had the highest revenue?
3. What is the average order value by customer segment?
4. Create visualizations for sales trends over time
5. Identify any seasonal patterns in the data

### Requirements:
- Use pandas for data manipulation
- Create at least 3 different types of charts (bar, line, scatter)
- Include statistical analysis (mean, median, correlation)
- Provide insights and recommendations

### Submission:
- Jupyter notebook with your analysis
- Include markdown cells explaining your findings
- Ensure all code runs without errors
      `,
      submissions: 42,
      averageScore: 87.3,
      createdAt: '2024-11-28T16:20:00Z',
      updatedAt: '2024-12-05T11:45:00Z',
      createdBy: 'instructor@skillzen.com',
      tags: ['python', 'data-analysis', 'pandas', 'visualization'],
      category: 'data-science',
      rubric: [
        {
          criteria: 'Data Analysis Accuracy',
          maxPoints: 35,
          description: 'Correct analysis and answers to the given questions',
        },
        {
          criteria: 'Code Quality',
          maxPoints: 20,
          description: 'Clean, efficient, and well-commented code',
        },
        {
          criteria: 'Visualizations',
          maxPoints: 15,
          description: 'Clear and informative charts and graphs',
        },
        {
          criteria: 'Insights & Recommendations',
          maxPoints: 10,
          description: 'Meaningful insights and business recommendations',
        },
      ],
    },
  ],

  questionBank: [
    {
      id: 'qbank_1',
      type: 'multiple_choice',
      question: 'What is the output of console.log(typeof null) in JavaScript?',
      options: ['null', 'undefined', 'object', 'boolean'],
      correctAnswer: 2,
      explanation: 'In JavaScript, typeof null returns "object". This is a known quirk of the language.',
      difficulty: 'intermediate',
      category: 'javascript',
      tags: ['javascript', 'data-types', 'typeof'],
      points: 1,
      usageCount: 12,
      createdAt: '2024-11-10T08:30:00Z',
      updatedAt: '2024-11-10T08:30:00Z',
    },
    {
      id: 'qbank_2',
      type: 'true_false',
      question: 'React components must return a single root element.',
      correctAnswer: false,
      explanation: 'Since React 16, components can return multiple elements using React.Fragment or an array.',
      difficulty: 'intermediate',
      category: 'react',
      tags: ['react', 'components', 'jsx'],
      points: 1,
      usageCount: 8,
      createdAt: '2024-11-12T14:15:00Z',
      updatedAt: '2024-11-12T14:15:00Z',
    },
    {
      id: 'qbank_3',
      type: 'fill_in_blank',
      question: 'In SQL, the _____ clause is used to filter records based on a condition.',
      correctAnswer: 'WHERE',
      explanation: 'The WHERE clause in SQL is used to specify conditions for filtering records.',
      difficulty: 'beginner',
      category: 'sql',
      tags: ['sql', 'database', 'filtering'],
      points: 1,
      usageCount: 15,
      createdAt: '2024-11-08T10:45:00Z',
      updatedAt: '2024-11-08T10:45:00Z',
    },
    {
      id: 'qbank_4',
      type: 'multiple_answer',
      question: 'Which of the following are Python data structures?',
      options: ['List', 'Dictionary', 'Set', 'Array', 'Tuple'],
      correctAnswers: [0, 1, 2, 4],
      explanation: 'List, Dictionary, Set, and Tuple are built-in data structures in Python. Array is not a built-in data structure (though arrays can be created using the array module).',
      difficulty: 'beginner',
      category: 'python',
      tags: ['python', 'data-structures'],
      points: 2,
      usageCount: 9,
      createdAt: '2024-11-14T09:20:00Z',
      updatedAt: '2024-11-14T09:20:00Z',
    },
    {
      id: 'qbank_5',
      type: 'code_completion',
      question: 'Complete the following Python function to calculate the factorial of a number:',
      codeTemplate: `def factorial(n):
    if n == 0 or n == 1:
        return 1
    else:
        return _____ * _____`,
      correctAnswer: 'n * factorial(n-1)',
      explanation: 'The factorial function uses recursion: factorial(n) = n * factorial(n-1)',
      difficulty: 'intermediate',
      category: 'python',
      tags: ['python', 'recursion', 'algorithms'],
      points: 3,
      usageCount: 6,
      createdAt: '2024-11-16T13:10:00Z',
      updatedAt: '2024-11-16T13:10:00Z',
    },
  ],

  quizAnalytics: [
    {
      quizId: 'quiz_1',
      totalAttempts: 245,
      averageScore: 78.5,
      passRate: 82.4, // percentage
      averageTimeSpent: 18.5, // minutes
      completionRate: 94.3, // percentage
      questionAnalytics: [
        {
          questionId: 'q1',
          correctAnswers: 198,
          totalAnswers: 245,
          accuracyRate: 80.8,
          averageTime: 45, // seconds
        },
        {
          questionId: 'q2',
          correctAnswers: 167,
          totalAnswers: 245,
          accuracyRate: 68.2,
          averageTime: 52,
        },
        {
          questionId: 'q3',
          correctAnswers: 203,
          totalAnswers: 245,
          accuracyRate: 82.9,
          averageTime: 38,
        },
      ],
      difficultyBreakdown: {
        easy: { questions: 4, averageScore: 85.2 },
        medium: { questions: 4, averageScore: 72.8 },
        hard: { questions: 2, averageScore: 58.3 },
      },
      timeDistribution: {
        '0-10min': 45,
        '10-20min': 98,
        '20-30min': 87,
        '30min+': 15,
      },
    },
  ],

  categories: [
    {
      id: 'programming',
      name: 'Programming',
      description: 'General programming concepts and languages',
      color: '#3b82f6',
    },
    {
      id: 'frontend',
      name: 'Frontend Development',
      description: 'HTML, CSS, JavaScript, React, Vue, etc.',
      color: '#22c55e',
    },
    {
      id: 'backend',
      name: 'Backend Development',
      description: 'Server-side development, APIs, databases',
      color: '#f59e0b',
    },
    {
      id: 'data-science',
      name: 'Data Science',
      description: 'Data analysis, machine learning, statistics',
      color: '#8b5cf6',
    },
    {
      id: 'devops',
      name: 'DevOps',
      description: 'Deployment, CI/CD, cloud services',
      color: '#ef4444',
    },
    {
      id: 'design',
      name: 'Design',
      description: 'UI/UX design, graphics, user experience',
      color: '#ec4899',
    },
    {
      id: 'mobile',
      name: 'Mobile Development',
      description: 'iOS, Android, React Native, Flutter',
      color: '#06b6d4',
    },
    {
      id: 'security',
      name: 'Cybersecurity',
      description: 'Security practices, ethical hacking',
      color: '#84cc16',
    },
  ],

  questionTypes: [
    {
      id: 'multiple_choice',
      name: 'Multiple Choice',
      description: 'Single correct answer from multiple options',
      icon: 'CircleDot',
    },
    {
      id: 'multiple_answer',
      name: 'Multiple Answer',
      description: 'Multiple correct answers from options',
      icon: 'CheckSquare',
    },
    {
      id: 'true_false',
      name: 'True/False',
      description: 'Simple true or false question',
      icon: 'ToggleLeft',
    },
    {
      id: 'fill_in_blank',
      name: 'Fill in the Blank',
      description: 'Complete the sentence or statement',
      icon: 'Edit3',
    },
    {
      id: 'short_answer',
      name: 'Short Answer',
      description: 'Brief written response',
      icon: 'FileText',
    },
    {
      id: 'essay',
      name: 'Essay',
      description: 'Long-form written response',
      icon: 'Book',
    },
    {
      id: 'code_completion',
      name: 'Code Completion',
      description: 'Complete the given code snippet',
      icon: 'Code',
    },
    {
      id: 'matching',
      name: 'Matching',
      description: 'Match items from two lists',
      icon: 'GitBranch',
    },
  ],

  difficultyLevels: [
    {
      id: 'beginner',
      name: 'Beginner',
      description: 'Basic concepts and introductory material',
      color: '#22c55e',
      pointMultiplier: 1,
    },
    {
      id: 'intermediate',
      name: 'Intermediate',
      description: 'Moderate difficulty with some complexity',
      color: '#f59e0b',
      pointMultiplier: 1.5,
    },
    {
      id: 'advanced',
      name: 'Advanced',
      description: 'Complex concepts requiring deep understanding',
      color: '#ef4444',
      pointMultiplier: 2,
    },
    {
      id: 'expert',
      name: 'Expert',
      description: 'Highly complex, expert-level material',
      color: '#8b5cf6',
      pointMultiplier: 3,
    },
  ],
};
